/**
 * AuditSchedulesPage — Sec 9 yearly + Sec 14F.
 *
 * Year picker + grid of audit schedules. Each card downloads a PDF.
 * Schedules: debtor confirmation, creditor confirmation, fixed asset register,
 *            inventory valuation, loan & interest schedule.
 */
import { useState } from 'react'
import {
  App, Button, Card, Col, Empty, Grid, Row, Select, Space, Spin, Typography,
} from 'antd'
import { DownloadOutlined, FilePdfOutlined, FileSearchOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const { useBreakpoint } = Grid

interface Schedule {
  key: string
  label: string
  description: string
}

export default function AuditSchedulesPage() {
  const { message: msg } = App.useApp()
  const { plantId, plants, setPlantId } = usePlantId()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  // Indian FY: 1 Apr → 31 Mar. fy=2025 means 2025-26.
  const currentFy = dayjs().month() >= 3 ? dayjs().year() : dayjs().year() - 1
  const [fy, setFy] = useState<number>(currentFy)
  const [downloading, setDownloading] = useState<string | null>(null)

  const { data: catalogue = [], isLoading } = useQuery<Schedule[]>({
    queryKey: ['audit-schedule-catalogue'],
    queryFn: () => apiClient.get('/audit-schedules/catalogue').then(r => r.data),
  })

  const fyOptions = Array.from({ length: 10 }, (_, i) => {
    const year = currentFy - i
    return { value: year, label: `FY ${year}-${(year + 1).toString().slice(-2)}` }
  })

  const downloadSchedule = async (s: Schedule) => {
    setDownloading(s.key)
    try {
      const res = await apiClient.get(`/audit-schedules/${s.key}.pdf`, {
        params: { plant_id: plantId, fy },
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${s.key}_FY${fy}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      msg.success(`${s.label} downloaded`)
    } catch (e: any) {
      const detail = e.response?.data instanceof Blob
        ? await e.response.data.text().then((t: string) => { try { return JSON.parse(t).detail } catch { return null } })
        : e.response?.data?.detail
      msg.error(detail || `Failed to download ${s.label}`)
    } finally {
      setDownloading(null)
    }
  }

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <FileSearchOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Year-End Audit Schedules
      </Title>
      <Text type="secondary">
        Auditor-ready PDFs for year-end statutory audit. Confirmation letters,
        fixed asset register, inventory valuation, loan schedules.
      </Text>

      <Card style={{ marginTop: 16 }} styles={{ body: { padding: isMobile ? 12 : 24 } }}>
        <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} md="auto">
            <Select value={plantId} onChange={setPlantId}
              style={{ width: isMobile ? '100%' : 220 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
          </Col>
          <Col xs={24} md="auto">
            <Select value={fy} onChange={setFy}
              style={{ width: isMobile ? '100%' : 180 }}
              options={fyOptions} />
          </Col>
          <Col flex="auto" />
          <Col>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {fy && `Period: 1 Apr ${fy} → 31 Mar ${fy + 1}`}
            </Text>
          </Col>
        </Row>

        {isLoading ? <Spin /> : catalogue.length === 0 ? (
          <Empty description="No schedules available" />
        ) : (
          <Row gutter={[12, 12]}>
            {catalogue.map(s => (
              <Col key={s.key} xs={24} sm={12} md={12} lg={8}>
                <Card hoverable
                  styles={{ body: { padding: 16 } }}
                  style={{ height: '100%' }}>
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    <Space>
                      <FilePdfOutlined style={{ color: '#cf1322', fontSize: 22 }} />
                      <Text strong style={{ fontSize: 14 }}>{s.label}</Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {s.description}
                    </Text>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      block
                      size={isMobile ? 'large' : 'middle'}
                      loading={downloading === s.key}
                      disabled={!plantId}
                      onClick={() => downloadSchedule(s)}
                      style={{ marginTop: 8 }}
                    >
                      Download PDF
                    </Button>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <div style={{ marginTop: 24, padding: 12, background: '#fffbe6',
          border: '1px solid #ffe58f', borderRadius: 6 }}>
          <Text style={{ fontSize: 12 }}>
            <b>Note:</b> Schedules are generated on-demand from current ledger
            data. For confirmation letters, the auditor's name and address need
            to be filled in before sending. Loan schedule will populate once the
            loan amortisation module is configured.
          </Text>
        </div>
      </Card>
    </div>
  )
}
